import React from 'react'
import { useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Select from 'src/views/components/Select'
import SelectInput from '../components/forms/formik/SelectInput'

interface SemesterSelectProps {
  readonly isFormInput?: boolean
  /** Disabled select showing semester from URL or active semester (e.g. add-course page). */
  readonly disabledPreview?: boolean
}

export default function SemesterSelect({ isFormInput = false, disabledPreview = false }: SemesterSelectProps) {
  const t = useTranslatable()
  const activeSemester = useActiveSemester()
  const [searchParams, setSearchParams] = useSearchParams({
    semId: activeSemester?.id as string,
  })
  const semId = searchParams.get('semId')
  const headers = useAuthenticatedHeaders()

  const { data: semesters, isValidating: isValidatingSemesters } = useApi({
    endpoint: Api.getSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
    revalidateIfStale: false,
  })

  const selectedSemester = React.useMemo(
    () => semesters?.filter((semester) => semester.id === semId)[0],
    [semesters, semId]
  )
  const semesterOptions = React.useMemo(() => {
    if (!semesters) return [{ value: '', label: t('common:any') }]
    return [{ value: '', label: t('common:any') }, ...semesters.map((s) => ({ value: s.id, label: s.name }))]
  }, [semesters, t])

  const previewSemId = semId ?? activeSemester?.id ?? null
  const previewSelectedSemester = React.useMemo(
    () =>
      previewSemId == null || previewSemId === '' || semesters == null
        ? null
        : (semesters.find((s) => String(s.id) === String(previewSemId)) ?? null),
    [previewSemId, semesters]
  )

  if (disabledPreview) {
    return (
      <div className="w-full min-w-0 sm:max-w-md">
        <label className="mb-1 block text-sm font-medium text-primaryTextColor" htmlFor="add-course-semester-preview">
          {t('common:semester')}
        </label>
        <Select
          inputId="add-course-semester-preview"
          className="my-0 w-full sm:w-[70%] xxs:max-w-full xs:max-w-full"
          value={previewSelectedSemester}
          options={semesters ?? []}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name + (option.isActive ? t('common:current') : '')}
          onChange={() => {}}
          isDisabled
          isLoading={isValidatingSemesters}
        />
      </div>
    )
  }

  if (isFormInput) {
    return (
      <SelectInput
        label={t('common:semester')}
        name="semId"
        options={semesterOptions}
        disabled={isValidatingSemesters}
        getOptionLabel={(option) => option.label + (option.value === activeSemester?.id ? t('common:current') : '')}
      />
    )
  }

  return (
    <Select
      className="my-2 mr-2 w-full sm:w-[70%] xxs:max-w-full xs:max-w-full"
      value={selectedSemester}
      options={semesters ?? []}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.name + (option.isActive ? t('common:current') : '')}
      onChange={(option) => setSearchParams({ semId: option!.id })}
      isDisabled={isValidatingSemesters}
    />
  )
}
